import { TOKEN_KEY } from '@/constants/auth.ts'
import router from '@/router'
import { userStore } from '@/stores/userStore.ts'
import axios from 'axios'

export const axiosClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  paramsSerializer: (params) =>
    new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined)),
    ).toString(),
})

axiosClient.interceptors.request.use(
  (config) => {
    const accessToken = localStorage.getItem(TOKEN_KEY)
    if (accessToken) {
      const cleanToken = accessToken.trim()
      // Only set Authorization header if token contains valid ASCII / ISO-8859-1 characters
      if (/^[\x20-\x7E]+$/.test(cleanToken)) {
        config.headers.Authorization = `Bearer ${cleanToken}`
      } else {
        console.warn('Invalid characters in access_token, removing from localStorage.')
        localStorage.removeItem(TOKEN_KEY)
      }
    }
    return config
  },
  (error) => Promise.reject(error),
)

let isRefreshing = false
export const isAuthRefreshing = () => isRefreshing
let failedQueue: Array<{ resolve: (token: string) => void; reject: (err: any) => void }> = []

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error)
    } else if (token) {
      prom.resolve(token)
    }
  })
  failedQueue = []
}

axiosClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config
    const { logout } = userStore()

    if (error.response && (error.response.status === 401 || error.response.status === 403)) {
      const url = originalRequest?.url || ''
      const isPublicAuthEndpoint =
        url.includes('/auth/login') ||
        url.includes('/auth/refresh') ||
        url.includes('/auth/verify-invitation') ||
        url.includes('/auth/accept-invitation') ||
        url.includes('/auth/reset-password') ||
        url.includes('/auth/check-recovery-code')

      if (!isPublicAuthEndpoint) {
        if (error.response.status === 403 || error.response.data?.message?.toLowerCase().includes('deactivated')) {
          const deactMsg = error.response.data?.message || 'Your account is deactivated. Please, contact your admin.'
          sessionStorage.setItem('deactivated_message', deactMsg)
          logout()
          await router.push('/auth/login')
          return Promise.reject(error)
        }

        if (error.response.status === 401 && !originalRequest._retry) {
          if (isRefreshing) {
            return new Promise((resolve, reject) => {
              failedQueue.push({ resolve, reject })
            })
              .then((token) => {
                originalRequest.headers.Authorization = `Bearer ${token}`
                return axiosClient(originalRequest)
              })
              .catch((err) => Promise.reject(err))
          }

          originalRequest._retry = true
          isRefreshing = true

          try {
            const response = await axios.post(
              `${import.meta.env.VITE_API_BASE_URL}/auth/refresh`,
              {},
              { withCredentials: true },
            )

            const { accessToken: newAccessToken } = response.data
            localStorage.setItem(TOKEN_KEY, newAccessToken)

            axiosClient.defaults.headers.common.Authorization = `Bearer ${newAccessToken}`
            originalRequest.headers.Authorization = `Bearer ${newAccessToken}`

            processQueue(null, newAccessToken)
            return axiosClient(originalRequest)
          } catch (refreshError) {
            processQueue(refreshError, null)
            logout()
            await router.push('/auth/login')
            return Promise.reject(refreshError)
          } finally {
            isRefreshing = false
          }
        }

        logout()
        await router.push('/auth/login')
      }
    }

    return Promise.reject(error)
  },
)
